/**
 * Dialog, Confirm, and typed-confirm (docs/03 §5, docs/04 §3). One dialog
 * depth — nothing opens a modal on a modal; complex flows get routes
 * (docs/03 §1). Radix supplies the a11y plumbing (focus trap, escape,
 * aria wiring), as docs/08 sanctions.
 */

import * as RadixDialog from '@radix-ui/react-dialog';
import { useState, type ReactNode } from 'react';

import { Button } from './Button.js';

/**
 * Width. `sm` (480px) is the default and covers confirms and short forms; `lg`
 * (720px) exists for content with an intrinsic aspect ratio — the photo cropper
 * and the photo lightbox — where 480px would letterbox a 16:9 frame down to
 * something too small to judge.
 */
export type DialogSize = 'sm' | 'lg';

const WIDTH: Record<DialogSize, string> = {
  sm: 'w-[min(92vw,480px)]',
  lg: 'w-[min(94vw,720px)]',
};

export function Dialog({
  open,
  onOpenChange,
  title,
  children,
  footer,
  size = 'sm',
  error,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly children: ReactNode;
  readonly footer?: ReactNode;
  readonly size?: DialogSize;
  /**
   * A rejected submission's message, rendered IN the dialog (docs/03 §2).
   *
   * This prop exists because the alternative kept losing people's work: every
   * onError handler was closing the dialog before raising a toast, so a 409 or
   * a validation rejection threw away a whole typed form. The dialog now stays
   * open with its values intact and says what went wrong where the user is
   * looking — and a toast is the wrong vehicle for that anyway (docs/04 §7:
   * errors needing anchoring do not go to toasts).
   */
  readonly error?: string | null | undefined;
}): ReactNode {
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 bg-brand-950/40" />
        <RadixDialog.Content
          className={
            'fixed left-1/2 top-1/2 max-h-[92vh] -translate-x-1/2 -translate-y-1/2 ' +
            'overflow-y-auto rounded-dialog bg-card p-6 shadow-lg ' +
            WIDTH[size]
          }
        >
          <RadixDialog.Title className="font-display text-h2 text-ink">{title}</RadixDialog.Title>
          <div className="mt-3 text-body text-ink2">{children}</div>
          {error === undefined || error === null || error === '' ? null : (
            <p
              role="alert"
              className="mt-4 rounded-ctl bg-red-bg px-3 py-2 text-meta font-medium text-red-fg"
            >
              {error}
            </p>
          )}
          {footer !== undefined ? (
            <div className="mt-5 flex justify-end gap-2">{footer}</div>
          ) : null}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}

/** Standard confirm — destructive actions that are recoverable (docs/03 §5). */
export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  confirmLabel,
  onConfirm,
  busy = false,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly body: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly busy?: boolean;
}): ReactNode {
  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={title}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button variant="primary" busy={busy} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      {body}
    </Dialog>
  );
}

/**
 * Typed confirmation — ONLY for hard-delete/purge (docs/03 §5). The noun the
 * user must type is the record's name, so the friction scales with intent.
 */
export function TypedConfirmDialog({
  open,
  onOpenChange,
  title,
  body,
  requiredText,
  confirmLabel,
  onConfirm,
  busy = false,
}: {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly title: string;
  readonly body: string;
  readonly requiredText: string;
  readonly confirmLabel: string;
  readonly onConfirm: () => void;
  readonly busy?: boolean;
}): ReactNode {
  const [typed, setTyped] = useState('');
  const match = typed === requiredText;
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setTyped('');
        onOpenChange(next);
      }}
      title={title}
      footer={
        <>
          <Button
            variant="ghost"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            Cancel
          </Button>
          <Button
            variant="danger-ghost"
            busy={busy}
            disabledReason={match ? undefined : `Type "${requiredText}" to enable`}
            onClick={onConfirm}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{body}</p>
      <label className="mt-3 block">
        <span className="mb-1 block text-meta font-medium text-ink2">
          Type <strong className="text-ink">{requiredText}</strong> to confirm
        </span>
        <input
          value={typed}
          onChange={(event) => {
            setTyped(event.target.value);
          }}
          className="keeper-ignore w-full rounded-ctl border border-control-line bg-card px-3 py-2 text-body text-ink"
          autoComplete="off"
        />
      </label>
    </Dialog>
  );
}
